import { useEffect, useRef, useState } from 'react';
import { C } from '@ridgewill/brand';
import { authClient } from '../lib/auth-client.js';
import { isUsernameAvailable, removeAvatar, uploadAvatar } from '../lib/api.js';
import { Field, Problem, Note, button, input, notACredentialField } from '../components/form.js';
import { TesterCard } from '../sections/TesterCard.js';
import { BirthDate } from '../sections/BirthDate.js';

type User = {
  email: string;
  name?: string | null;
  preferredName?: string | null;
  username?: string | null;
  displayUsername?: string | null;
  image?: string | null;
};

/**
 * ONE USERNAME, TWO COLUMNS, AND THEY ARE NOT INTERCHANGEABLE (#8).
 *
 * Better Auth's username plugin stores `username` LOWER-CASED and keeps what
 * was actually typed in `displayUsername`. Everything that compares - sign-in,
 * uniqueness, `/is-username-available` - uses the normalised one, and the
 * display one exists purely so "Jon" does not come back as "jon".
 *
 * So a difference of capitals is not a different name, and this is the function
 * that has to know it. Exported and pure for the same reason `isActive` is.
 */
export function isSameUsername(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Name, username and avatar.
 *
 * ── Username availability is checked as you type ─────────────────────
 *
 * `/is-username-available` exists for this. Submitting and being told "taken"
 * is the same information arriving after the effort rather than during it, and
 * the effort here is choosing a name.
 *
 * Debounced, because the alternative is a request per keystroke against the
 * service that also signs everyone in. The check is advisory: the server still
 * rejects a collision on save, because between the check and the save somebody
 * else can take it.
 */
export function Profile({ user, onChanged }: { user: User; onChanged: () => void }) {
  const [name, setName] = useState(user.name ?? '');
  const [preferredName, setPreferredName] = useState(user.preferredName ?? '');
  /**
   * `displayUsername` FIRST. It is what the person typed; `username` is the
   * lower-cased copy the server compares against. Seeding the box from the
   * normalised one silently rewrites "Jon" to "jon" the moment the page loads,
   * and then every later comparison is against the wrong value.
   */
  const current = user.displayUsername ?? user.username ?? '';
  const [username, setUsername] = useState(current);
  /**
   * READ-ONLY UNTIL ASKED. There is no input on the page until this is true,
   * which is the entire point - see `UsernameField`.
   */
  const [editingUsername, setEditingUsername] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty =
    name !== (user.name ?? '') || preferredName !== (user.preferredName ?? '') || username !== current;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setSaving(true);
    /**
     * Username goes through `/update-user` like any other field — there is no
     * `/change-username` endpoint, despite the name being the sort of thing
     * that usually has one. The account deployment's allow-list claimed
     * otherwise until this screen went looking for it.
     */
    const wanted = username.trim();
    /**
     * BOTH columns, always. `/update-user` given only `username` writes the
     * normalised column and leaves `displayUsername` exactly as it was - see
     * the plugin's update hook, which spreads `displayUsername` only when it is
     * present in the body. Sent once at sign-up and never again, the two drift
     * permanently: the comparisons move to your new name while everything that
     * DISPLAYS a name keeps showing the old one.
     */
    /**
     * ONLY WHEN IT CHANGED. Sent every time, an untouched username is
     * re-validated on each save, and one set before the 4-character minimum
     * ("Jon") refused every other edit on this page with "Username is too
     * short". Exact comparison, not `isSameUsername`: a change of capitals is
     * still a change to `displayUsername`.
     */
    const usernameChanged = wanted !== '' && wanted !== current;
    const { error: err } = await authClient.updateUser({
      name: name.trim(),
      // Blank clears it: the server stores '' as null (RidgeWill/auth#677).
      preferredName: preferredName.trim(),
      ...(usernameChanged ? { username: wanted, displayUsername: wanted } : {}),
    });
    setSaving(false);
    if (err) {
      setError(err.message ?? 'That could not be saved.');
      return;
    }
    setSaved(true);
    setEditingUsername(false);
    onChanged();
  }

  return (
    <section style={{ maxWidth: 520 }}>
      <h2 style={{ fontSize: '1.15rem', marginBottom: '1rem' }}>Profile</h2>

      <Avatar image={user.image} name={user.name} email={user.email} onChanged={onChanged} />

      <form onSubmit={save} style={{ marginTop: '1.5rem' }}>
        {/*
          TWO NAMES, SIDE BY SIDE ON A DESKTOP (RidgeWill/auth#677).

          "Full name" is the one `name`, shown wherever a person is listed.
          "What should we call you?" is how apps greet them, asked for rather
          than guessed: the first word of a full name is the surname for anyone
          whose family name comes first. Blank means "the first word is fine",
          which the placeholder shows.

          `auto-fit` rather than a media query: the pair wraps to one column
          whenever the section is too narrow for two, phone or otherwise.
        */}
        <div style={nameRow}>
          <Field id="name" label="Full name">
            <input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={input}
              {...notACredentialField}
            />
          </Field>
          <Field id="preferred-name" label="What should we call you?">
            <input
              id="preferred-name"
              value={preferredName}
              onChange={(e) => setPreferredName(e.target.value)}
              placeholder={firstWord(name)}
              maxLength={PREFERRED_NAME_MAX}
              aria-describedby="preferred-name-note"
              style={input}
              {...notACredentialField}
            />
            <Note id="preferred-name-note">Optional. How apps greet you.</Note>
          </Field>
        </div>

        <UsernameField
          value={username}
          current={current}
          editing={editingUsername}
          onChange={setUsername}
          onEdit={() => setEditingUsername(true)}
          onCancel={() => {
            setUsername(current);
            setEditingUsername(false);
          }}
        />

        {/*
          The address is shown, not edited, and the note now points somewhere
          rather than apologising (account#54).

          Changing it is `/change-email`, which sends a link and changes nothing
          on its own — a FLOW rather than a field. Putting it in this form would
          imply Save changes it, when Save only starts it.

          It lives on the Security screen rather than here because the address
          is a credential on this service: every provider sign-in resolves
          through it (RidgeWill/auth#324), the change is gated like a credential
          change, and every fact those gates are decided on — whether there is a
          password, a passkey, a linked provider — is already loaded over there.
          A change started here would also show its confirmation, and the cancel
          control for it, on a page the person had navigated away from.
        */}
        <Field id="email" label="Email">
          <input id="email" value={user.email} readOnly disabled style={{ ...input, opacity: 0.7 }} />
          <Note>
            Changing your email address happens on the{' '}
            {/* A real link, not a button that pushes a route: this app routes by
                pathname on full page loads. See App.tsx. */}
            <a href="/security#email" style={{ color: C.brand }}>
              Security page
            </a>
            , which asks for your password and sends a link to the new address.
          </Note>
        </Field>

        {error && <Problem>{error}</Problem>}
        {saved && !dirty && <Note tone="ok">Saved.</Note>}

        <button type="submit" disabled={saving || !dirty} style={button}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </form>

      {/*
        Outside the form: changing it is a modal with its own rules and its
        own Save, and none of it is saved by Save changes (auth#672).
      */}
      <BirthDate />

      {/*
        Last, and outside the form: it has its own buttons, and none of them is
        saved by Save changes (platform#56).
      */}
      <TesterCard user={user} />
    </section>
  );
}

function UsernameField({
  value,
  current,
  editing,
  onChange,
  onEdit,
  onCancel,
}: {
  value: string;
  current: string;
  editing: boolean;
  onChange: (v: string) => void;
  onEdit: () => void;
  onCancel: () => void;
}) {
  const [state, setState] = useState<'idle' | 'checking' | 'free' | 'taken'>('idle');
  const box = useRef<HTMLInputElement | null>(null);

  // Focus on the way in, so pressing Edit puts the caret where it is wanted
  // rather than making somebody click a second time.
  useEffect(() => {
    if (editing) box.current?.focus();
  }, [editing]);

  useEffect(() => {
    if (!editing) {
      setState('idle');
      return;
    }
    const wanted = value.trim();
    /**
     * Unchanged or empty asks nothing - your own name is not "taken" by you.
     *
     * CASE-INSENSITIVELY unchanged, which was the bug in #8. `/is-username-
     * available` lower-cases and then asks whether ANY user holds it, with no
     * notion of "except you". So retyping "Jon" when the stored name is already
     * "jon" is not a change at all, but a case-sensitive `!==` treats it as one,
     * asks the server, gets your own record back, and reports "That username is
     * taken." about the name you are already using.
     */
    if (!wanted || isSameUsername(wanted, current)) {
      setState('idle');
      return;
    }
    setState('checking');
    let cancelled = false;
    const t = setTimeout(() => {
      void isUsernameAvailable(wanted)
        .then((free) => {
          // A late reply from an earlier keystroke must not overwrite a newer
          // answer — otherwise the field flickers to a verdict about a name the
          // person has already changed.
          if (!cancelled) setState(free ? 'free' : 'taken');
        })
        .catch(() => {
          // The check is advisory. If it fails, say nothing rather than
          // blocking a save the server would have accepted.
          if (!cancelled) setState('idle');
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [value, current, editing]);

  /**
   * NOT A TEXT BOX UNTIL YOU ASK FOR ONE.
   *
   * A password manager fills what looks like a credential field on page load.
   * This one was marked `autocomplete="username"`, the saved credential for the
   * site has an EMAIL as its username, and so the box quietly showed an email
   * address over the top of the stored value. It reads as "my username did not
   * save" - which is what it was reported as - while the database holds exactly
   * what was typed.
   *
   * Rendering no input at all until Edit is pressed is what actually fixes
   * that, rather than an `autocomplete` hint a filler is free to ignore: there
   * is nothing on the page to fill. It also means the stored value is always
   * the one on screen, so if a filler DOES overwrite the box after Edit, the
   * change is visible as a change rather than looking like the truth.
   */
  if (!editing) {
    return (
      /* No `id`: see `Field`. Pointing the label at the button made the whole
         row a trigger. */
      <Field label="Username">
        <div style={readOnlyRow}>
          <span style={readOnlyValue}>{current || <em style={{ color: C.textMid }}>Not set</em>}</span>
          <button type="button" onClick={onEdit} style={editButton} aria-label="Edit username">
            Edit
          </button>
        </div>
      </Field>
    );
  }

  return (
    <Field id="username" label="Username">
      <input
        id="username"
        ref={box}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={input}
        /**
         * `autocomplete="off"` plus one attribute per password manager. This is
         * a profile editor, not a sign-in form, and naming it a credential field
         * is what invited the fill in the first place. See `notACredentialField`.
         */
        {...notACredentialField}
      />
      {state === 'checking' && <Note>Checking…</Note>}
      {state === 'free' && <Note tone="ok">Available.</Note>}
      {state === 'taken' && <Note tone="bad">That username is taken.</Note>}
      <button type="button" onClick={onCancel} style={{ ...button, marginRight: '.5rem' }}>
        Cancel
      </button>
    </Field>
  );
}

/** The server's limit (RidgeWill/auth#677), so the box stops where a save would fail. */
const PREFERRED_NAME_MAX = 50;

/** What a blank preferred name falls back to, for the placeholder. */
export function firstWord(name: string): string {
  return name.trim().split(/\s+/)[0] ?? '';
}

const nameRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(13rem, 1fr))',
  columnGap: '1rem',
  alignItems: 'start',
};

const readOnlyRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '.75rem',
  flexWrap: 'wrap',
};

/** Looks like the value it is, not like a box somebody failed to type in.
 *
 *  `overflow-wrap: anywhere` rather than `word-break: break-all`: the latter
 *  cuts the value at whatever character the line runs out on even when the
 *  whole thing would have fitted on the next one. `anywhere` also keeps this
 *  flex item from forcing the row wide, which `break-word` would not.
 *
 *  NOT `Address` from @ridgewill/brand/react, which the email rows use: this
 *  row holds a USERNAME. That component's whole job is preferring `@` and `.`
 *  as break points, and a username has neither to prefer. */
const readOnlyValue: React.CSSProperties = {
  flex: '1 1 12rem',
  padding: '.6rem 0',
  fontSize: '1rem',
  overflowWrap: 'anywhere',
};

const editButton: React.CSSProperties = { ...button, marginTop: 0, padding: '.45rem .9rem' };

/**
 * The avatar: shown, uploaded, removed.
 *
 * ── The file input accepts three types, and the server decides ───────
 *
 * `accept` is a CONVENIENCE, not a control — it filters the picker and nothing
 * else, and a determined caller posts whatever they like straight to the
 * endpoint. The real check is server-side on the magic bytes, because these
 * objects are served publicly from a subdomain of the domain the session cookie
 * is scoped to. SVG is refused there by name; see the auth service's lib/avatar.ts.
 *
 * So the list here matches the server's on purpose. A picker offering SVG and a
 * server refusing it would make the error look like a bug.
 */
function Avatar({
  image,
  name,
  email,
  onChanged,
}: {
  image?: string | null;
  name?: string | null;
  email: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initial = (name?.trim() || email).charAt(0).toUpperCase();

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Clear immediately so choosing the SAME file again re-fires change —
    // otherwise a failed upload cannot be retried without picking something
    // else first, which reads as the button being broken.
    e.target.value = '';
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      await uploadAvatar(file);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That image could not be uploaded.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setError(null);
    setBusy(true);
    try {
      await removeAvatar();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be removed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        {image ? (
          <img
            src={image}
            alt=""
            width={64}
            height={64}
            style={{ borderRadius: '50%', objectFit: 'cover' }}
            /* A social-sign-in avatar is on a third-party CDN. No referrer, so
               visiting your own account page is not announced to whoever hosts
               the picture. Harmless for our own R2 URLs. */
            referrerPolicy="no-referrer"
          />
        ) : (
          <div aria-hidden style={placeholder}>
            {initial}
          </div>
        )}

        <div>
          <label style={{ ...button, marginTop: 0, display: 'inline-block' }}>
            {busy ? 'Working…' : image ? 'Change picture' : 'Upload a picture'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => void pick(e)}
              disabled={busy}
              style={{ display: 'none' }}
            />
          </label>
          {image && (
            <button type="button" onClick={() => void remove()} disabled={busy} style={{ ...button, marginTop: 0, marginLeft: '.5rem' }}>
              Remove
            </button>
          )}
          <Note>PNG, JPEG or WebP, up to 1 MB.</Note>
        </div>
      </div>
      {error && <Problem>{error}</Problem>}
    </div>
  );
}

const placeholder: React.CSSProperties = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  background: C.inputBorder,
  fontSize: '1.5rem',
};
